/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import urlJoin from 'proper-url-join';

import { endpoints } from '@ibm-watson/shared/modules/ia/util/config';
import { PaginatedResult, StoreQueryParams } from '../../../../ts';
import { SkillReference } from '../../../../ts/assistant';
import { ReactivateSession } from '../../../../ts/session';
import { Skill, SkillReferenceType } from '../../../../ts/skill';
import { Snapshot } from '../../../../ts/snapshot';
import { getAccessPrefixedUrl } from '../../util/access-control';

export interface PaginatedSnapshots extends PaginatedResult {
  snapshots: Snapshot[];
}

const auditParams: StoreQueryParams = {
  include_audit: true,
  verbose: true
};

const skillEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  urlJoin(
    endpoints.store,
    getAccessPrefixedUrl(
      useNewIAUpdatedAccess,
      assistantId,
      `skills/${skillId}`
    )
  );

const snapshotsEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  urlJoin(
    skillEndpoint(skillId, useNewIAUpdatedAccess, assistantId),
    'snapshots'
  );

const snapshotEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  snapshotId: string
) =>
  urlJoin(
    snapshotsEndpoint(skillId, useNewIAUpdatedAccess, assistantId),
    snapshotId
  );

const skillReferenceEndpoint = (id: string) =>
  urlJoin(endpoints.store, 'v2', 'assistants', id, 'skill_references');

export const listSnapshots = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  reactivateSession?: ReactivateSession
): Promise<PaginatedSnapshots> =>
  axios
    .get<PaginatedSnapshots>(
      urlJoin(snapshotsEndpoint(skillId, useNewIAUpdatedAccess, assistantId), {
        query: {
          reactivateSession
        }
      }),
      {
        params: auditParams
      }
    )
    .then(response => response.data);

export const createSnapshot = async (
  skillId: string,
  description: string,
  async: boolean
): Promise<Snapshot> => {
  // CJK update with Publish AC
  const response = await axios.post<Snapshot>(
    snapshotsEndpoint(skillId, false, ''),
    { description },
    {
      params: {
        ...auditParams,
        async
      }
    }
  );
  return response.data;
};

export const exportSnapshot = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  snapshotId: string
): Promise<Skill> => {
  const response = await axios.get<Skill>(
    snapshotEndpoint(skillId, useNewIAUpdatedAccess, assistantId, snapshotId),
    {
      params: {
        ...auditParams,
        export: true,
        async: false
      }
    }
  );
  return response.data;
};

export const getSnapshot = async (
  skillId: string,
  snapshotId: string
): Promise<Snapshot> => {
  const response = await axios.get<Snapshot>(
    snapshotEndpoint(skillId, false, '', snapshotId),
    {
      params: {
        ...auditParams
      }
    }
  );

  return response.data;
};

export const deleteSnapshot = async (
  skillId: string,
  snapshotId: string
): Promise<{}> => {
  const response = await axios.delete(
    snapshotEndpoint(skillId, false, '', snapshotId)
  );

  return response.data;
};
export const assignSnapshotToAssistant = async (
  assistantId: string,
  skillId: string,
  snapshotId: string
): Promise<SkillReference> => {
  const response = await axios.post<SkillReference>(
    skillReferenceEndpoint(assistantId),
    {
      skill_id: skillId,
      snapshot: snapshotId
    },
    { params: auditParams }
  );
  return response.data;
};

export const updateSnapshotInAssistant = async (
  assistantId: string,
  skillReference: SkillReferenceType,
  snapshotId: string
): Promise<SkillReference> => {
  const url = skillReferenceEndpoint(assistantId);
  const response = await axios.post<SkillReference>(
    urlJoin(url, skillReference),
    { snapshot: snapshotId },
    {
      params: { ...auditParams }
    }
  );
  return response.data;
};

export const revertSnapshot = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  snapshotId: string
): Promise<Skill> => {
  const response = await axios.post<Skill>(
    skillEndpoint(skillId, useNewIAUpdatedAccess, assistantId),
    {
      source: {
        skill_id: skillId,
        snapshot: snapshotId
      }
    },
    { params: auditParams }
  );
  return response.data;
};
